<template>
  <div class="p-6 dark:text-gray-50" v-if="canAdd || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-1.5 rounded-md hover:bg-gray-700 text-sm flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
            clip-rule="evenodd"
          />
        </svg>
        Back
      </button>
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEditMode ? 'Edit' : 'Add' }} SNMP Metric Profile</h1>
    </div>

    <form
      @submit.prevent="submitForm"
      class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
    >
      <!-- Profile Name -->
      <div class="flex flex-col">
        <label for="name" class="mb-1 font-medium">
          Profile Name<span class="text-red-500">*</span>
        </label>
        <input
          id="name"
          v-model="formData.name"
          type="text"
          required
          placeholder="e.g., cisco_routers, basic_system"
          :class="inputClass('name')"
        />
        <p class="text-sm text-gray-500 mt-1">
          Must start with a letter. Only letters, numbers, and underscores allowed.
        </p>
        <FieldError :message="fieldErrors.name" />
      </div>

      <!-- Description -->
      <div class="flex flex-col">
        <label for="description" class="mb-1 font-medium">
          Description <span class="text-gray-500 text-sm">(optional)</span>
        </label>
        <textarea
          id="description"
          v-model="formData.description"
          rows="3"
          placeholder="Describe what devices or metrics this profile monitors..."
          class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        ></textarea>
      </div>

      <!-- Organisation -->
      <div class="flex flex-col">
        <label for="org" class="mb-1 font-medium">
          Organisation <span class="text-gray-500 text-sm">(leave blank for global)</span>
        </label>
        <GlassSelect
          v-model="formData.org"
          :options="organisations"
          :searchable="true"
          valueKey="id"
          labelKey="name"
          placeholder="Global (visible to all orgs)"
          :error="!!fieldErrors.org"
        />
        <p class="text-sm text-gray-500 mt-1">
          Global profiles are visible to all organisations. Org-specific profiles are only visible to that org.
        </p>
        <FieldError :message="fieldErrors.org" />
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-2 pt-4">
        <button type="button" @click="goBack" class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
          Cancel
        </button>
        <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          {{ isEditMode ? 'Update' : 'Create' }} Profile
        </button>
      </div>
    </form>
  </div>

  <PageError v-else-if="permsLoaded" :error="'Permission denied'" :errorCode="'403'" />
</template>

<script>
import { createSNMPProfile, getSNMPProfileById, updateSNMPProfile } from "@/services/snmpProfileService";
import { getAllOrgs } from "@/services/orgService";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddSNMPProfile",
  components: { FieldError, PageError, GlassSelect },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      fieldErrors: {},
      formData: {
        name: "",
        description: "",
        org: null,
      },
      organisations: [],
      canAdd: false,
      isLoading: false,
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    profileId() {
      return this.$route.params.id;
    },
    isEditMode() {
      return !!this.profileId;
    },
    currentSchema() {
      return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null;
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.checkPermissions();
    await this.loadOrganisations();
    if (this.isEditMode) {
      await this.loadExistingProfile();
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add SNMP Metric Profile");
      }
    },
    async loadOrganisations() {
      try {
        this.organisations = await getAllOrgs();
      } catch (error) {
        console.error("Error loading organisations:", error);
      }
    },
    async loadExistingProfile() {
      this.isLoading = true;
      try {
        const profile = await getSNMPProfileById(this.profileId, this.currentSchema);
        this.formData.name = profile.name;
        this.formData.description = profile.description || "";
        this.formData.org = profile.org;
      } catch (error) {
        console.error("Error loading profile:", error);
        alert("Failed to load profile data");
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    inputClass(field) {
      return [
        "border rounded px-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600",
        this.fieldErrors[field] ? "border-red-500 ring-2 ring-red-500" : "border-gray-300 focus:ring-blue-500",
        this.isEditMode && field === "name" ? "bg-gray-100 dark:bg-gray-700 cursor-not-allowed" : "",
      ];
    },
    goBack() {
      if (this.isEditMode) {
        this.$router.push({ name: "SNMPProfileDetails", params: { id: this.profileId }, query: this.tenant.addSchemaParam() });
      } else {
        this.$router.push({ name: "SNMPProfiles", query: this.tenant.addSchemaParam() });
      }
    },
    async submitForm() {
      this.fieldErrors = {};

      // Validate profile name format
      const nameRegex = /^[a-zA-Z][a-zA-Z0-9_]*$/;
      if (!nameRegex.test(this.formData.name)) {
        this.fieldErrors.name = "Name must start with a letter and contain only letters, numbers, and underscores";
        return;
      }

      const data = {
        name: this.formData.name,
        description: this.formData.description,
        org: this.formData.org,
      };

      try {
        if (this.isEditMode) {
          await updateSNMPProfile(this.profileId, data, this.currentSchema);
          // After update, go back to details with pillar notice flag
          this.$router.push({
            name: "SNMPProfileDetails",
            params: { id: this.profileId },
            query: { ...this.tenant.addSchemaParam(), showPillarNotice: true }
          });
        } else {
          const created = await createSNMPProfile(data, this.currentSchema);
          // After create, go to details with pillar notice flag
          this.$router.push({
            name: "SNMPProfileDetails",
            params: { id: created.id },
            query: { ...this.tenant.addSchemaParam(), showPillarNotice: true }
          });
        }
      } catch (error) {
        const errData = error?.response?.data;
        if (errData) {
          Object.keys(errData).forEach((key) => {
            this.fieldErrors[key] = Array.isArray(errData[key]) ? errData[key][0] : errData[key];
          });
        }
        console.error(`Error ${this.isEditMode ? "updating" : "creating"} profile:`, error);
      }
    },
  },
};
</script>
